<script setup lang="ts">
import iconPicture from '~/assets/images/logo.png'
import DeploymentMaintenanceBanner from '~/components/app/DeploymentMaintenanceBanner.vue'
import SourceButton from '~/components/ui/SourceButton.vue'
import SourceTag from '~/components/ui/SourceTag.vue'
import { useDeploymentMaintenance } from '~/composables/api/useDeploymentMaintenance'

const route = useRoute()
const { loggedIn } = useOidcAuth()
const { state: maintenanceState } = useDeploymentMaintenance()

/** Nav principale limitée (Header Source - ne pas saturer). */
const navItems = [
  { to: '/dev', label: 'Accueil', exact: true },
  { to: '/dev/design/components', label: 'Composants', exact: false },
  { to: '/dev/toasts', label: 'Toasts', exact: false },
  { to: '/dev/oidc-profiles', label: 'OIDC', exact: false },
  { to: '/dev/gardian', label: 'Gardian', exact: false },
  { to: '/dev/maintenance', label: 'Maintenance', exact: false },
] as const

function isNavActive(path: string, exact?: boolean): boolean {
  const current = route.path.replace(/\/$/, '') || '/'
  const target = path.replace(/\/$/, '') || '/'
  if (exact) {
    return current === target
  }
  return current === target || current.startsWith(`${target}/`)
}
function goToApplication(): void {
  void navigateTo('/')
}
</script>

<template>
  <div class="dev-layout">
    <!--
      Header Source - style white, size medium, applications internes :
      hasLogo + hasTitle + hasActions + hasBottomSection + hasSeparator
    -->
    <header class="dev-layout__header" role="banner">
      <DeploymentMaintenanceBanner :state="maintenanceState" />

      <div class="dev-layout__header-shell">
        <div class="dev-layout__header-top">
          <NuxtLink to="/dev" class="dev-layout__brand">
            <img
              class="dev-layout__brand-logo"
              :src="iconPicture"
              width="40"
              height="40"
              alt=""
            >
            <span class="dev-layout__brand-titles">
              <span class="dev-layout__brand-text">Mon Orga</span>
              <SourceTag
                class="dev-layout__brand-lab"
                text="Labo"
                type="primary"
                size="xsmall"
                theme="light"
                flat
              />
            </span>
          </NuxtLink>

          <div class="dev-layout__actions">
            <div
              class="dev-layout__status"
              :class="loggedIn ? 'dev-layout__status--in' : 'dev-layout__status--out'"
              role="status"
            >
              <span class="dev-layout__status-dot" aria-hidden="true" />
              <span>{{ loggedIn ? 'Session connectée' : 'Non connecté' }}</span>
            </div>
            <SourceButton
              text="Application"
              type="secondary"
              size="small"
              icon="home"
              @click="goToApplication"
            />
          </div>
        </div>

        <nav class="dev-layout__nav" aria-label="Navigation laboratoire">
          <ul class="dev-layout__nav-list">
            <li v-for="item in navItems" :key="item.to">
              <NuxtLink
                :to="item.to"
                class="dev-layout__nav-link"
                :class="{ 'is-active': isNavActive(item.to, item.exact) }"
              >
                {{ item.label }}
              </NuxtLink>
            </li>
          </ul>
        </nav>
      </div>
    </header>

    <main id="dev-main" class="dev-layout__main">
      <slot />
    </main>

    <footer class="dev-layout__footer" role="contentinfo">
      <div class="dev-layout__footer-inner">
        <div class="dev-layout__footer-brand">
          <img
            class="dev-layout__footer-logo"
            :src="iconPicture"
            width="32"
            height="32"
            alt=""
          >
          <div class="dev-layout__footer-text-block">
            <span class="dev-layout__footer-title">Mon Orga</span>
            <span class="dev-layout__footer-sub">Laboratoire développement</span>
          </div>
        </div>
        <p class="dev-layout__footer-hint">Pages non incluses en production · DevTools : Mon Orga Lab</p>
      </div>
    </footer>
  </div>
</template>

<style scoped lang="scss">
@use '~/assets/scss/partials/elpdl/colors' as c;
@use '~/assets/scss/partials/elpdl/spacing' as *;
@use '~/assets/scss/partials/elpdl/elevation' as elev;

$dev-content-max: 1200px;

.dev-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  color: c.$enedis-neutral-800;
  background-color: c.$enedis-neutral-30;
  font-family: 'Public Sans', Enedis, sans-serif;
}

/* -- Header Source white -- */
.dev-layout__header {
  flex-shrink: 0;
  background: c.$enedis-neutral-10;
  @include elev.elevation-050;
}

.dev-layout__header-shell {
  max-width: $dev-content-max;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  padding: 0 $spacing-xl;
}

.dev-layout__header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $spacing-m;
  min-width: 0;
  min-height: 64px;
  padding: $spacing-s 0;
  border-bottom: 1px solid c.$enedis-neutral-40;
}

.dev-layout__brand {
  display: inline-flex;
  align-items: center;
  gap: $spacing-s;
  text-decoration: none;
  min-width: 0;
  border-radius: 8px;
  padding: 4px;
  margin: -4px;

  &:hover {
    background: c.$enedis-neutral-20;
  }

  &:focus-visible {
    outline: 2px solid c.$enedis-primary-500;
    outline-offset: 2px;
  }
}

.dev-layout__brand-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex-shrink: 0;
  display: block;
}

.dev-layout__brand-titles {
  display: inline-flex;
  align-items: center;
  gap: $spacing-2xs;
  min-width: 0;
}

.dev-layout__brand-text {
  font-family: Enedis, sans-serif;
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.2;
  color: c.$enedis-primary-500;
  letter-spacing: -0.01em;
}

.dev-layout__brand-lab {
  flex-shrink: 0;
}

.dev-layout__actions {
  display: inline-flex;
  align-items: center;
  gap: $spacing-m;
  flex-shrink: 0;
}

.dev-layout__status {
  display: inline-flex;
  align-items: center;
  gap: $spacing-2xs;
  font-size: 0.75rem;
  font-weight: 600;
  padding: $spacing-2xs $spacing-xs;
  border-radius: 4px;
  border: 1px solid c.$enedis-neutral-60;
  line-height: 1.2;
  white-space: nowrap;
}

.dev-layout__status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dev-layout__status--in {
  background: c.$enedis-success-200;
  color: c.$enedis-success-700;
  border-color: c.$enedis-success-300;
}

.dev-layout__status--in .dev-layout__status-dot {
  background: c.$enedis-success-500;
}

.dev-layout__status--out {
  background: c.$enedis-neutral-20;
  color: c.$enedis-neutral-600;
  border-color: c.$enedis-neutral-60;
}

.dev-layout__status--out .dev-layout__status-dot {
  background: c.$enedis-neutral-400;
}

/* Bottom section - nav soulignée (pattern Tabs underlined Source, hors composant Tabs) */
.dev-layout__nav {
  min-width: 0;
}

.dev-layout__nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: $spacing-m;
  overflow-x: auto;
  scrollbar-width: thin;
  border-bottom: 1px solid c.$enedis-neutral-60;
}

.dev-layout__nav-link {
  display: inline-flex;
  align-items: center;
  margin: 0 0 -1px;
  padding: $spacing-2xs $spacing-s $spacing-s;
  border: 2px solid transparent;
  border-bottom: 3px solid transparent;
  border-radius: 4px 4px 0 0;
  font-family: 'Public Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  color: c.$enedis-neutral-400;
  text-decoration: none;
  white-space: nowrap;
  transition:
    color 0.1s ease,
    background-color 0.1s ease,
    border-color 0.1s ease;

  &:hover {
    background-color: c.$enedis-primary-300;
    color: c.$enedis-primary-500;
  }

  &:focus-visible {
    outline: none;
    background-color: c.$enedis-primary-300;
    border-color: c.$enedis-primary-400;
    color: c.$enedis-primary-500;
  }

  &.is-active {
    border-bottom-color: c.$enedis-primary-500;
    color: c.$enedis-primary-500;
    background-color: transparent;
    font-weight: 700;
  }

  &.is-active:focus-visible {
    border-bottom-color: c.$enedis-primary-500;
  }
}

.dev-layout__main {
  flex: 1;
  min-height: 0;
  padding: $spacing-xl $spacing-xl $spacing-2xl;
  max-width: $dev-content-max;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

.dev-layout__footer {
  flex-shrink: 0;
  margin-top: auto;
  background: c.$enedis-neutral-10;
  border-top: 1px solid c.$enedis-neutral-40;
}

.dev-layout__footer-inner {
  max-width: $dev-content-max;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  padding: $spacing-m $spacing-xl;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: $spacing-m;
}

.dev-layout__footer-brand {
  display: flex;
  align-items: center;
  gap: $spacing-m;
  min-width: 0;
}

.dev-layout__footer-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  flex-shrink: 0;
  display: block;
}

.dev-layout__footer-text-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.dev-layout__footer-title {
  font-family: Enedis, sans-serif;
  font-size: 0.9375rem;
  font-weight: 700;
  color: c.$enedis-neutral-900;
  line-height: 1.2;
}

.dev-layout__footer-sub {
  font-size: 0.8125rem;
  font-weight: 500;
  color: c.$enedis-neutral-500;
  line-height: 1.25;
}

.dev-layout__footer-hint {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: c.$enedis-neutral-400;
  line-height: 1.3;
}

@media (max-width: 720px) {
  .dev-layout__header-shell {
    padding: 0 $spacing-m;
  }

  .dev-layout__header-top {
    flex-wrap: wrap;
    min-height: 0;
    padding: $spacing-s 0;
  }

  .dev-layout__actions {
    width: 100%;
    justify-content: space-between;
  }

  .dev-layout__nav-list {
    gap: $spacing-s;
  }

  .dev-layout__main {
    padding: $spacing-l $spacing-m $spacing-xl;
  }

  .dev-layout__footer-inner {
    flex-direction: column;
    align-items: flex-start;
    padding: $spacing-m;
  }
}
</style>
